// src/components/MapboxGL/CenterMarkerOverlay.jsx
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react'
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import IconPoint from '../../assets/images/icon-tree/2.png'
import { useBoundaryDetection } from '../../hooks/useBoundaryDetection'
import { AlertContext } from '../../context/AlertContext'
import { isPointInBoundary } from '../../utils/geoBoundaryValidation'

// --- Componente principal ---

const CenterMarkerOverlay = ({
  map,
  initialCoords = null,
  onConfirm,
  onCancel,
  selectedTenant = null,
  tenantBoundary = null,
  tenantBoundaryLoading = false,
  tenantIsGeneric = false,
}) => {
  const { alertRef } = useContext(AlertContext)
  const { validateCoordinatesForCity, currentCity } = useBoundaryDetection()

  const [centerCoords, setCenterCoords] = useState(null)
  const [gpsLoading, setGpsLoading] = useState(false)
  const [validating, setValidating] = useState(false)

  // Refs para estabilidad en callbacks asíncronos
  const selectedTenantRef = useRef(selectedTenant)
  const tenantBoundaryRef = useRef(tenantBoundary)
  const tenantBoundaryLoadingRef = useRef(tenantBoundaryLoading)
  const tenantIsGenericRef = useRef(tenantIsGeneric)
  const validateForCityRef = useRef(validateCoordinatesForCity)
  const currentCityRef = useRef(currentCity)

  selectedTenantRef.current = selectedTenant
  tenantBoundaryRef.current = tenantBoundary
  tenantBoundaryLoadingRef.current = tenantBoundaryLoading
  tenantIsGenericRef.current = tenantIsGeneric
  validateForCityRef.current = validateCoordinatesForCity
  currentCityRef.current = currentCity

  // Volar a coordenadas existentes al montar
  useEffect(() => {
    if (map && initialCoords?.lng && initialCoords?.lat) {
      map.flyTo({
        center: [initialCoords.lng, initialCoords.lat],
        zoom: Math.max(map.getZoom(), 17),
        essential: true,
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Rastrear centro del mapa en vivo (throttleado con rAF)
  useEffect(() => {
    if (!map) return
    let rafId = null

    const handleMove = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const c = map.getCenter()
        setCenterCoords({ lng: c.lng, lat: c.lat })
      })
    }

    handleMove()
    map.on('move', handleMove)
    return () => {
      map.off('move', handleMove)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [map])

  // Validación combinada: ciudad + tenant
  const validateCoordinates = useCallback(async coordinates => {
    const cityValidation = await validateForCityRef.current(coordinates)
    if (!cityValidation.isValid) return cityValidation

    const tenant = selectedTenantRef.current
    const loading = tenantBoundaryLoadingRef.current
    const boundary = tenantBoundaryRef.current
    const isGeneric = tenantIsGenericRef.current

    if (tenant) {
      if (loading) {
        return {
          isValid: false,
          city: currentCityRef.current,
          message: 'Cargando límites del tenant, intente de nuevo en un momento...',
        }
      }
      if (isGeneric) {
        // Tenant genérico: sin restricción geográfica
      } else if (!boundary) {
        return {
          isValid: false,
          city: currentCityRef.current,
          message:
            'El tenant no tiene límites geográficos definidos. No se puede registrar árboles.',
        }
      } else if (!isPointInBoundary(coordinates, boundary)) {
        return {
          isValid: false,
          city: currentCityRef.current,
          message: 'Este punto está fuera de los límites del tenant asignado',
        }
      }
    }

    return cityValidation
  }, [])

  // GPS: obtener ubicación del dispositivo y volar al punto
  const handleGPS = useCallback(() => {
    if (!navigator.geolocation) {
      alertRef?.current?.message('GPS no disponible en este navegador')
      alertRef?.current?.isSeverity('warning')
      alertRef?.current?.openAlert(true)
      return
    }

    setGpsLoading(true)
    navigator.geolocation.getCurrentPosition(
      position => {
        setGpsLoading(false)
        map.flyTo({
          center: [position.coords.longitude, position.coords.latitude],
          zoom: Math.max(map.getZoom(), 17),
          essential: true,
        })
      },
      error => {
        setGpsLoading(false)
        const messages = {
          1: 'Permiso de ubicación denegado. Actívalo en la configuración del navegador.',
          2: 'No se pudo determinar tu ubicación. Verifica que el GPS esté activado.',
          3: 'Tiempo de espera agotado al obtener la ubicación.',
        }
        alertRef?.current?.message(
          messages[error.code] || 'Error al obtener ubicación GPS',
        )
        alertRef?.current?.isSeverity('warning')
        alertRef?.current?.openAlert(true)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    )
  }, [map, alertRef])

  // Confirmar: validar y capturar coordenadas del centro
  const handleConfirm = useCallback(async () => {
    if (!centerCoords) return
    setValidating(true)

    const validation = await validateCoordinates([centerCoords.lng, centerCoords.lat])
    setValidating(false)

    if (!validation.isValid) {
      alertRef?.current?.message(validation.message)
      alertRef?.current?.isSeverity('warning')
      alertRef?.current?.openAlert(true)
      return
    }

    alertRef?.current?.message(`Ubicación capturada en ${validation.city?.toUpperCase()}`)
    alertRef?.current?.isSeverity('success')
    alertRef?.current?.openAlert(true)

    onConfirm({ lng: centerCoords.lng, lat: centerCoords.lat })
  }, [centerCoords, validateCoordinates, alertRef, onConfirm])

  return (
    <>
      {/* Mira central fija (centrada exactamente en el punto de captura) */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 10,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
          animation: 'crosshairDrop 0.3s ease-out',
        }}>
        <img src={IconPoint} alt="" style={{ width: 54, height: 54, opacity: 0.9 }} />
      </Box>

      {/* Punto de referencia bajo el pin */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'rgba(0,0,0,0.3)',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: 9,
        }}
      />

      {/* Barra de captura flotante */}
      <Paper
        elevation={6}
        sx={{
          position: 'absolute',
          bottom: { xs: 16, md: 24 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, md: 1 },
          px: { xs: 1, md: 2 },
          py: 0.75,
          borderRadius: 3,
          bgcolor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.15)',
          maxWidth: { xs: '95vw', md: 'auto' },
        }}>
        {/* GPS */}
        <Tooltip title="Usar GPS del dispositivo" placement="top">
          <span>
            <IconButton
              onClick={handleGPS}
              disabled={gpsLoading}
              size="small"
              sx={{ color: gpsLoading ? 'grey.500' : '#64b5f6' }}>
              {gpsLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <MyLocationIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
        />

        {/* Coordenadas en vivo */}
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: { xs: '0.65rem', md: '0.8rem' },
            color: 'rgba(255,255,255,0.9)',
            minWidth: { xs: 100, md: 180 },
            textAlign: 'center',
            userSelect: 'none',
            px: 0.5,
          }}>
          {centerCoords
            ? `${centerCoords.lng.toFixed(6)}, ${centerCoords.lat.toFixed(6)}`
            : '...'}
        </Typography>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
        />

        {/* Confirmar */}
        <Tooltip title="Confirmar ubicación" placement="top">
          <span>
            <IconButton
              onClick={handleConfirm}
              disabled={validating || !centerCoords}
              size="small"
              sx={{ color: validating ? 'grey.500' : '#4caf50' }}>
              {validating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/* Cancelar */}
        <Tooltip title="Cancelar captura" placement="top">
          <IconButton onClick={onCancel} size="small" sx={{ color: '#f44336' }}>
            <CancelIcon />
          </IconButton>
        </Tooltip>
      </Paper>
    </>
  )
}

export default CenterMarkerOverlay
